import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import "./NewLearningJourney.css";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIosNew";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import axios from "axios";
import { FaTag, FaLock, FaCheckCircle, FaBookOpen } from "react-icons/fa";
import { ProductContext } from "../context/AppContext";
import FooterNav from "./Common_Components/FooterNav";
import translations from "./translation";

function NewLearningJourney() {
  const navigate = useNavigate();
  const location = useLocation();
  const { Leveldata, ExamTypedata, Universitydata, DaysLeft, Translate } = useContext(ProductContext);

  const selectedLanguage = localStorage.getItem("selectedLanguage") || "hindi";
  const localTranslations = translations[selectedLanguage === "hindi" ? "hindi" : "english"];
  const authToken = localStorage.getItem("AuthToken");

  const apiBaseUrl = process.env.REACT_APP_BASE_URL || "https://api.japaking.online";
  const [isLoading, setIsLoading] = useState(false);
  const [reorderedExams, setReorderedExams] = useState([]);
  const lastClickTimeRef = useRef(0);

  // Lessons details list from detail endpoint
  const [lessons, setLessons] = useState([]);
  // Set of day/lesson IDs that have been completed
  const [completedDayIds, setCompletedDayIds] = useState(new Set());
  // List of all user visits for detailed screen completion tracking
  const [visitsList, setVisitsList] = useState([]);
  // The latest visited section details for highlighting
  const [latestVisitData, setLatestVisitData] = useState(null);
  // The next section details for highlighting
  const [nextSectionData, setNextSectionData] = useState(null);

  // Locking/Subscription states
  const [hasSubscribed, setHasSubscribed] = useState(false);
  const [couponVerified, setCouponVerified] = useState(false);
  const [subscriptionDaysLeft, setSubscriptionDaysLeft] = useState(0);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [selectedUniversity, setSelectedUniversity] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");

  const [activeItem, setActiveItem] = useState(null);
  const [activeItemName, setActiveItemName] = useState("");

  const { examTypes } = useContext(ProductContext);
  const [selectedExamId, setSelectedExamId] = useState(() => Number(localStorage.getItem("selectedExamId")) || null);
  const exams = Array.isArray(examTypes) ? examTypes : [];

  // 1. Initialize Subscriptions and active exam
  const checkSubscription = useCallback(async () => {
    try {
      if (!authToken) return;
      const response = await fetch(
        `${apiBaseUrl}/api/user-subscription`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch user subscription data");
      }

      const data = await response.json();

      if (data && data.success) {
        const now = new Date();
        const activePlans = data.message.plan.filter(p => {
          const activity = p.Activity_Status ? String(p.Activity_Status).toLowerCase() : "";
          return activity === "active" ||
            activity === "trial" ||
            activity === "trail" ||
            (new Date(p.start_date) <= now && new Date(p.end_date) >= now && p.status === "active");
        });

        if (activePlans.length > 0) {
          const endDate = new Date(activePlans[0].end_date);
          const today = new Date();
          const timeDiff = endDate.getTime() - today.getTime();
          const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
          setSubscriptionDaysLeft(daysDiff > 0 ? daysDiff : 0);
        } else {
          setSubscriptionDaysLeft(0);
        }
        const hasActiveSubscription = activePlans.length > 0;

        if (hasActiveSubscription) {
          localStorage.setItem("couponVerified", "true");
        } else {
          const verified = localStorage.getItem("couponVerified") === "true";
          if (!verified) {
            localStorage.removeItem("couponVerified");
          }
        }

        setHasSubscribed(hasActiveSubscription);
      } else {
        setHasSubscribed(false);
        if (localStorage.getItem("couponVerified") !== "true") {
          localStorage.removeItem("couponVerified");
        }
        setSubscriptionDaysLeft(0);
      }
    } catch (err) {
      console.error("Error in checkSubscription:", err);
    }
  }, [authToken]);

  useEffect(() => {
    if (authToken) {
      checkSubscription();
    }
  }, [authToken, checkSubscription]);

  // Read coupon from local storage
  useEffect(() => {
    const verified = localStorage.getItem("couponVerified") === "true";
    setCouponVerified(verified);
  }, []);

  // Initialize selectedExamId with dynamic reordering
  useEffect(() => {
    const initializeExamId = async () => {
      const stateExamId = location.state?.selectedExamId;
      const stateExamType = location.state?.selectedExamType;
      const matchedStateExamType = stateExamType
        ? exams.find((exam) => String(exam?.exam_type).toLowerCase() === String(stateExamType).toLowerCase())
        : null;

      let resolvedExamId = null;

      if (matchedStateExamType) {
        resolvedExamId = parseInt(matchedStateExamType.exam_id, 10);
      } else if (stateExamId) {
        resolvedExamId = parseInt(stateExamId, 10);
      } else {
        // Fallback: fetch latest visited status or localStorage
        let latestVisitExamId = null;
        if (authToken) {
          try {
            const response = await axios.get(
              `${apiBaseUrl}/api/user-visits/latest-visited-status`,
              {
                headers: {
                  Authorization: `Bearer ${authToken}`,
                  "Content-Type": "application/json",
                },
              }
            );
            const latestVisit = response?.data || null;
            if (latestVisit) {
              latestVisitExamId = Number(
                latestVisit.exam_id ||
                latestVisit.lessonDetails?.exam_id ||
                latestVisit.learningMethodDetails?.exam_id
              );
            }
          } catch (error) {
            console.error("Error fetching latest visited status:", error);
          }
        }

        const matchedLatestExam = latestVisitExamId
          ? exams.find((exam) => Number(exam.exam_id) === Number(latestVisitExamId))
          : null;

        if (matchedLatestExam) {
          resolvedExamId = Number(matchedLatestExam.exam_id);
        } else {
          const storedExamId = localStorage.getItem("selectedExamId");
          if (storedExamId) {
            resolvedExamId = parseInt(storedExamId, 10);
          } else if (exams.length > 0) {
            resolvedExamId = exams[0].exam_id;
          }
        }
      }

      if (resolvedExamId) {
        setSelectedExamId(resolvedExamId);
        localStorage.setItem("selectedExamId", resolvedExamId);
      }
    };

    if (exams && exams.length > 0) {
      initializeExamId();
    }
  }, [exams, location.state, authToken]);

  // Reorder tabs dynamically: put the active one first
  useEffect(() => {
    if (exams && exams.length > 0 && selectedExamId) {
      const activeIdx = exams.findIndex((exam) => Number(exam.exam_id) === Number(selectedExamId));
      if (activeIdx !== -1) {
        const reordered = [
          exams[activeIdx],
          ...exams.slice(0, activeIdx),
          ...exams.slice(activeIdx + 1)
        ];
        setReorderedExams(reordered);
      } else {
        setReorderedExams(exams);
      }
    } else {
      setReorderedExams(exams);
    }
  }, [exams, selectedExamId]);

  // 2. Fetch Journey Data (details for all lessons, latest-visit status, all user visits)
  const getJourneyDetails = async (examId) => {
    if (!examId || !authToken) return;
    setIsLoading(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      };

      // A. Fetch all lessons details (with learningMethods and screenTitle nested)
      const detailsUrl = `${apiBaseUrl}/api/exam-types/days/${examId}/details`;
      const detailsRes = await axios.get(detailsUrl, { headers });

      let fetchedDays = [];
      if (detailsRes.data.success && Array.isArray(detailsRes.data.message)) {
        fetchedDays = detailsRes.data.message;
      } else if (Array.isArray(detailsRes.data)) {
        fetchedDays = detailsRes.data;
      }

      // Sort days by day number / pono
      const sortedDays = fetchedDays.sort((a, b) => {
        const ponoDiff = Number(a?.pono || 0) - Number(b?.pono || 0);
        if (ponoDiff !== 0) return ponoDiff;

        const dayA = parseInt(String(a?.day_name || "").replace("Lesson", "").trim(), 10);
        const dayB = parseInt(String(b?.day_name || "").replace("Lesson", "").trim(), 10);
        return (isNaN(dayA) || isNaN(dayB)) ? 0 : dayA - dayB;
      });

      setLessons(sortedDays);

      // B. Fetch completed day/lesson IDs (matching /learning-journey visited-status API)
      const visitedUrl = `${apiBaseUrl}/api/user-visits/visited-status-raw?examId=${examId}`;
      const visitsRes = await axios.get(visitedUrl, { headers });

      const dayIds = new Set();
      const rawVisits = Array.isArray(visitsRes?.data) ? visitsRes.data : [];
      rawVisits.forEach((visit) => {
        if (visit?.unique_day_id || visit?.id) {
          dayIds.add(Number(visit.unique_day_id || visit.id));
        }
      });
      setVisitsList(rawVisits);
      setCompletedDayIds(dayIds);

      // C. Fetch the latest visited status for current section highlighting
      const latestUrl = `${apiBaseUrl}/api/user-visits/latest-visited-status`;
      const latestRes = await axios.get(latestUrl, { headers });
      setLatestVisitData(latestRes?.data || null);

      // D. Fetch the next section data
      try {
        const nextUrl = `${apiBaseUrl}/api/user-visits/next-section?examId=${examId}`;
        const nextRes = await axios.get(nextUrl, { headers });
        setNextSectionData(nextRes?.data || null);
      } catch (err) {
        console.error("Error fetching next section data:", err);
      }

    } catch (error) {
      console.error("Error loading journey details:", error);
      setLessons([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedExamId) {
      getJourneyDetails(selectedExamId);
    }
  }, [selectedExamId, authToken]);

  // Auto-scroll to the active/highlighted lesson screen card
  useEffect(() => {
    if (!isLoading && lessons.length > 0 && (latestVisitData || nextSectionData)) {
      const scrollTimer = setTimeout(() => {
        const highlightedEl = document.querySelector(".new-lj-screen-card.highlighted");
        if (highlightedEl) {
          highlightedEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 500);
      return () => clearTimeout(scrollTimer);
    }
  }, [lessons, latestVisitData, nextSectionData, isLoading]);

  // Tab click handler
  const handleExamTypeClick = (examId) => {
    const examIdInt = parseInt(examId, 10);
    if (isNaN(examIdInt)) return;

    setSelectedExamId(examIdInt);
    localStorage.setItem("selectedExamId", examIdInt);
  };

  // Locking evaluation
  const evaluateLockStatus = (index, dayName) => {
    const isCouponVerifiedLocal = localStorage.getItem("couponVerified") === "true";
    const hasSub = hasSubscribed || isCouponVerifiedLocal;

    // Lesson 0 and Lesson 1 are free
    const isFree =
      index === 0 ||
      index === 1 ||
      String(dayName).toLowerCase().includes("lesson 0") ||
      String(dayName).toLowerCase().includes("lesson 1") ||
      String(dayName).includes("पाठ 0") ||
      String(dayName).includes("पाठ 1");

    return !isFree && !hasSub;
  };

  // Navigation handler
  const handleLessonCardClick = (screen, method, lesson, isLocked) => {
    executeNavigation(screen, method, lesson, isLocked);
  };

  const executeNavigation = (screen, method, lesson, isLocked) => {
    if (isLocked) {
      setActiveItem(lesson.id);
      setActiveItemName(lesson.day_name);
      setIsModalOpen(true);
      return;
    }

    // Set the necessary localStorage items
    localStorage.setItem("ModuleId", String(lesson.id));
    localStorage.setItem("LearningJourneyActiveModuleId", String(lesson.id));
    localStorage.setItem("LearningId", String(method.learning_method_id));
    localStorage.setItem("LearningJourneyActiveLearningMethodId", String(method.learning_method_id));
    localStorage.setItem("day_learning_method_id", String(method.day_learning_method_id));
    localStorage.setItem("screenTitleId", String(screen.screen_title_id));
    localStorage.setItem("LearningJourneyActiveScreenId", String(screen.screen_title_id));
    localStorage.setItem("examId", String(selectedExamId));
    localStorage.setItem("selectedExamId", String(selectedExamId));
    localStorage.setItem("LearningJourneyActiveModuleName", String(lesson.day_name));
    localStorage.setItem("ModuleNameName", String(lesson.day_name));

    const title = String(screen.title || "").trim();
    const normalizedTitle = title.toLowerCase();
    let destinationScreen = "";

    console.log("executeNavigation inputs:", { screen, method, lesson, isLocked });

    const state = {
      categoryId: screen.category_id,
      dayId: method.day_id !== undefined && method.day_id !== null ? method.day_id : (lesson.day_id !== undefined && lesson.day_id !== null ? lesson.day_id : lesson.id),
      unique_day_id: lesson.id,
      ModuleId: lesson.id,
      LearningId: method.learning_method_id,
      day_learning_method_id: method.day_learning_method_id,
      examId: selectedExamId,
      is_review: screen.is_review,
      methodType: method.method_type,
      examTypes: exams,
      selectedExamId,
      screenTitleId: screen.screen_title_id,
      categoryName: screen.subtitle || method.desc || ""
    };

    if (
      normalizedTitle.includes("skit") ||
      normalizedTitle.includes("にほんのこうこう") ||
      normalizedTitle.includes("नाटक1_दिन") ||
      normalizedTitle.includes("नाटक")
    ) {
      destinationScreen = "/ModuleSkit";
    } else if (
      normalizedTitle === "quiz" ||
      normalizedTitle === "प्रश्न और उत्तर" ||
      normalizedTitle === "पढ़ना" ||
      normalizedTitle === "व्याकरण और शब्दावली" ||
      normalizedTitle === "grammar & vocabulary"
    ) {
      destinationScreen = "/take-quiz";
    } else if (
      normalizedTitle === "flash card" ||
      normalizedTitle === "flashcard" ||
      normalizedTitle === "फ्लैशकार्ड"
    ) {
      destinationScreen = "/FlashCard";
    } else if (normalizedTitle === "lecture" || normalizedTitle === "पाठ वीडियो") {
      destinationScreen = "/leacture-video";
    } else if (normalizedTitle === "writing practice" || normalizedTitle === "लिखाई का अभ्यास") {
      destinationScreen = "/PracticePage";
    } else if (normalizedTitle === "listening" || normalizedTitle === "सुनना") {
      destinationScreen = "/ModuleListening";
    } else if (normalizedTitle === "manga" || normalizedTitle.includes("जापानी कॉमिक्स") || normalizedTitle.includes("マンガ")) {
      destinationScreen = "/Manga";
    } else {
      destinationScreen = "/ModuleSkit";
    }

    navigate(destinationScreen, { state });
  };

  // Coupon handling
  const closeModal = () => {
    setIsModalOpen(false);
    setIsCouponModalOpen(false);
  };

  const openCouponModal = () => {
    setIsCouponModalOpen(true);
  };

  const verifyCoupon = useCallback(async () => {
    try {
      const url = `${apiBaseUrl}/api/coupon/subscribe`;
      const requestBody = {
        couponCode: couponCode.trim(),
        college_name: selectedUniversity,
        exam_type: selectedLevel,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (response.ok && result.message.success) {
        setCouponVerified(true);
        localStorage.setItem("couponVerified", "true");
        alert("Coupon Applied Successfully!");
        setIsModalOpen(false);
        setIsCouponModalOpen(false);
        await checkSubscription();

        // Re-load journey data
        if (selectedExamId) {
          getJourneyDetails(selectedExamId);
        }
      } else {
        console.error("Coupon verification failed:", result);
        alert("Incorrect Coupon Code or Exam Type");
      }
    } catch (error) {
      console.error("Error verifying coupon:", error);
    }
  }, [
    couponCode,
    selectedUniversity,
    selectedLevel,
    authToken,
    selectedExamId,
    checkSubscription
  ]);

  const getLevelRank = (examType) => {
    const type = String(examType || "").toUpperCase().trim();
    if (type.includes("N5")) return 1;
    if (type.includes("N4")) return 2;
    if (type.includes("N3")) return 3;
    if (type.includes("N2")) return 4;
    if (type.includes("N1")) return 5;
    return 99;
  };

  const sortedAllExams = [...exams].sort((a, b) => getLevelRank(a.exam_type) - getLevelRank(b.exam_type));
  const currentIndex = sortedAllExams.findIndex(exam => Number(exam.exam_id) === Number(selectedExamId));
  const currentExam = currentIndex !== -1 ? sortedAllExams[currentIndex] : null;
  const previousExam = currentIndex > 0 ? sortedAllExams[currentIndex - 1] : null;
  const nextExam = currentIndex !== -1 && currentIndex < sortedAllExams.length - 1 ? sortedAllExams[currentIndex + 1] : null;
  // Determine the resolved current section coordinates for highlighting
  let currentDayId = null;
  let currentMethodId = null;
  let currentScreenId = null;

  if (nextSectionData && nextSectionData.next_day_id) {
    const lat = nextSectionData.latestVisit;
    // Compare finished_at vs visitedAt timestamps to determine true finish status
    const openedTime = lat?.visitedAt ? new Date(lat.visitedAt).getTime() : 0;
    const finishedTime = lat?.finished_at ? new Date(lat.finished_at).getTime() : 0;
    // If finished_at is available, compare timestamps; otherwise fall back to finish boolean
    const isFinishedAfterOpened = lat?.finish && (
      finishedTime > 0 ? (finishedTime >= openedTime) : true
    );

    if (lat && !isFinishedAfterOpened) {
      // Highlight the unfinished latest visit (or re-opened after finishing)
      currentDayId = lat.unique_day_id || lat.id;
      currentMethodId = lat.learning_method_id;
      currentScreenId = lat.screen_title_id;
    } else {
      // Highlight the next section in order
      currentDayId = nextSectionData.next_day_id;
      currentMethodId = nextSectionData.next_learning_method_id || nextSectionData.learningMethod?.learning_method_id;
      currentScreenId = nextSectionData.next_screen_title_id || nextSectionData.screen?.screen_title_id;
    }
  } else if (latestVisitData) {
    currentDayId = latestVisitData.unique_day_id || latestVisitData.id;
    currentMethodId = latestVisitData.learning_method_id;
    currentScreenId = latestVisitData.screen_title_id;
  }

  return (
    <div className="new-learning-journey-page">
      {/* Top Navbar */}
      <div className="header_inner">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <ArrowBackIosIcon onClick={() => navigate("/")} className="icon_1" />
              <h2>
                <span className="active-banner-dot" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }}></span>
                Current Stage: {currentExam?.exam_type || "N5"}
              </h2>
              <ChatBubbleOutlineIcon
                onClick={() => navigate('/chat-with-staff')}
                className="logo_1"
                style={{ cursor: "pointer", color: "#ffc0cb" }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="new-lj-container">
        {/* Unified Learning Journey Timeline */}
        <div className="new-lj-journey-timeline">


          {/* Timeline Path starting after Active Card */}
          <div className="new-lj-timeline-path">

            {/* Previous Level Link (if exists) */}
            {previousExam && (
              <div className="new-lj-timeline-prev-step">
                <div className="new-lj-timeline-line"></div>
                <div className="new-lj-timeline-node prev">
                  {previousExam.exam_type}
                </div>
                <button
                  onClick={() => handleExamTypeClick(previousExam.exam_id)}
                  className="new-lj-roadmap-card prev"
                >
                  <span className="new-lj-roadmap-card-title">← Previous Level: {previousExam.exam_type}</span>
                </button>
              </div>
            )}

            {/* Stats details bar */}
            <div className="new-lj-stats-bar-wrapper">
              <div className="new-lj-timeline-line"></div>
              {/* <div className="new-lj-timeline-node stats">
                {currentExam?.exam_type}
              </div> */}
              {/* <div className="new-lj-stats-bar">
                <p className="new-lj-hint">Double click on any card to open the lecture, quiz, or writing practice</p>
              </div> */}
            </div>

            {/* Lessons List */}
            {isLoading ? (
              <div className="new-lj-loading-step">
                <div className="new-lj-timeline-line"></div>
                <div className="new-lj-timeline-node loading">
                  <div className="new-lj-timeline-node-inner"></div>
                </div>
                <div className="new-lj-loading">
                  <div className="new-lj-spinner"></div>
                  <p>Loading your curriculum...</p>
                </div>
              </div>
            ) : lessons.length > 0 ? (
              <div className="new-lj-timeline-lessons">
                {lessons.map((lesson, index) => {
                  const isLocked = evaluateLockStatus(index, lesson.day_name);
                  const isLessonLearned = Array.isArray(lesson.learningMethods) && lesson.learningMethods.length > 0
                    ? lesson.learningMethods.every((method) => {
                      return Array.isArray(method.screenTitle) && method.screenTitle.length > 0
                        ? method.screenTitle.every((screen) => {
                          return visitsList.some((visit) => {
                            return Number(visit.unique_day_id || visit.id) === Number(lesson.id) &&
                              Number(visit.learning_method_id) === Number(method.learning_method_id) &&
                              (!screen.screen_title_id || Number(visit.screen_title_id) === Number(screen.screen_title_id));
                          });
                        })
                        : visitsList.some((visit) => {
                          return Number(visit.unique_day_id || visit.id) === Number(lesson.id) &&
                            Number(visit.learning_method_id) === Number(method.learning_method_id);
                        });
                    })
                    : false;
                  let isAnyScreenCurrent = false;
                  if (currentDayId && currentMethodId && currentScreenId && Array.isArray(lesson.learningMethods)) {
                    isAnyScreenCurrent = lesson.learningMethods.some((method) => {
                      return Array.isArray(method.screenTitle) && method.screenTitle.some((screen) => {
                        return Number(currentDayId) === Number(lesson.id) &&
                          Number(currentMethodId) === Number(method.learning_method_id) &&
                          Number(currentScreenId) === Number(screen.screen_title_id);
                      });
                    });
                  }
                  const shouldLessonBeGrey = isLessonLearned && !isAnyScreenCurrent;

                  return (
                    <div key={lesson.id} className="new-lj-timeline-lesson-step">
                      <div className={`new-lj-timeline-line ${shouldLessonBeGrey ? "completed" : ""}`}></div>
                      <div className={`new-lj-timeline-node lesson ${isLocked ? "locked" : "active"} ${shouldLessonBeGrey ? "completed" : ""}`}>
                        {currentExam?.exam_type}
                      </div>

                      <div className={`new-lj-lesson-card ${isLocked ? "locked" : ""} ${shouldLessonBeGrey ? "completed-grey" : ""}`}>
                        {/* Lesson Header */}
                        <div className="new-lj-lesson-header">
                          <div className="new-lj-lesson-info">
                            <h3>{lesson.day_name}</h3>
                            <p>{lesson.desc || "Learn basic structures and grammar"}</p>
                          </div>
                          <div style={{ display: "flex", gap: "8px" }}>
                            {isLocked && (
                              <div className="new-lj-lock-badge">
                                <FaLock /> Locked
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Sub-Methods and Sub-Screens lists */}
                        <div className="new-lj-methods-container">
                          {Array.isArray(lesson.learningMethods) && lesson.learningMethods.map((method) => {
                            const isMethodLearned = Array.isArray(method.screenTitle) && method.screenTitle.length > 0
                              ? method.screenTitle.every((screen) => {
                                return visitsList.some((visit) => {
                                  return Number(visit.unique_day_id || visit.id) === Number(lesson.id) &&
                                    Number(visit.learning_method_id) === Number(method.learning_method_id) &&
                                    (!screen.screen_title_id || Number(visit.screen_title_id) === Number(screen.screen_title_id));
                                });
                              })
                              : visitsList.some((visit) => {
                                return Number(visit.unique_day_id || visit.id) === Number(lesson.id) &&
                                  Number(visit.learning_method_id) === Number(method.learning_method_id);
                              });
                            const isAnyScreenInMethodCurrent = latestVisitData &&
                              Array.isArray(method.screenTitle) &&
                              method.screenTitle.some((screen) => {
                                return Number(latestVisitData.unique_day_id || latestVisitData.id) === Number(lesson.id) &&
                                  Number(latestVisitData.learning_method_id) === Number(method.learning_method_id) &&
                                  Number(latestVisitData.screen_title_id) === Number(screen.screen_title_id);
                              });
                            const shouldMethodBeGrey = isMethodLearned && !isAnyScreenInMethodCurrent;

                            return (
                              <div key={method.learning_method_id} className={`new-lj-method-group ${shouldMethodBeGrey ? "completed-grey" : ""}`}>
                                <div className="new-lj-method-title">{method.method_type}</div>

                                <div className="new-lj-screens-grid">
                                  {Array.isArray(method.screenTitle) && method.screenTitle.map((screen) => {
                                    const isLearned = visitsList.some((visit) => {
                                      return Number(visit.unique_day_id || visit.id) === Number(lesson.id) &&
                                        Number(visit.learning_method_id) === Number(method.learning_method_id) &&
                                        (!screen.screen_title_id || Number(visit.screen_title_id) === Number(screen.screen_title_id));
                                    });
                                    const isCurrent = currentDayId && currentMethodId && currentScreenId &&
                                      Number(currentDayId) === Number(lesson.id) &&
                                      Number(currentMethodId) === Number(method.learning_method_id) &&
                                      Number(currentScreenId) === Number(screen.screen_title_id);

                                    return (
                                      <div
                                        key={screen.screen_title_id}
                                        onClick={() => handleLessonCardClick(screen, method, lesson, isLocked)}
                                        className={`new-lj-screen-card ${isCurrent ? "highlighted" : ""} ${isLearned && !isCurrent ? "learned" : ""}`}
                                      >
                                        <div>
                                          <h4 className="new-lj-screen-title">{screen.title}</h4>
                                          <p className="new-lj-screen-subtitle">{screen.subtitle || "Access this lesson module"}</p>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Separator between lessons */}
                      {index < lessons.length - 1 && (
                        <div className="new-lj-lesson-separator">
                          <div className="new-lj-lesson-separator-line"></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="new-lj-no-data-step">
                <div className="new-lj-timeline-line"></div>
                <div className="new-lj-timeline-node no-data">
                  <div className="new-lj-timeline-node-inner"></div>
                </div>
                <div className="new-lj-no-data">
                  <h4>No Lesson Data Found</h4>
                  <p>We couldn't retrieve lessons for this JLPT level.</p>
                </div>
              </div>
            )}

            {/* Next Level Link (if exists, shown at bottom) */}
            {nextExam && !isLoading && lessons.length > 0 && (
              <div className="new-lj-timeline-next-step">
                <div className="new-lj-timeline-line end"></div>
                <div className="new-lj-timeline-node next">
                  {nextExam.exam_type}
                </div>
                <button
                  onClick={() => handleExamTypeClick(nextExam.exam_id)}
                  className="new-lj-roadmap-card next"
                >
                  <span className="new-lj-roadmap-card-title">Next Level: {nextExam.exam_type} →</span>
                  <span className="new-lj-roadmap-card-action">Unlock pathway</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer Nav Bar */}
      <FooterNav authToken={authToken} translate={Translate} />

      {/* Locking overlay modal */}
      {!couponVerified && isModalOpen && (
        <div className="new-lj-modal-overlay">
          <div className="new-lj-modal">
            <button onClick={closeModal} className="new-lj-modal-close">×</button>
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/learning4.jpg`}
              alt="Subscription Required"
              className="new-lj-modal-img"
            />
            <h3>Subscription Required</h3>
            <p>Accessing lesson content from Lesson 2 onwards requires an active plan or a university coupon.</p>
            <button className="new-lj-modal-btn primary" onClick={() => navigate(window.ReactNativeWebView ? "/AppView" : "/plans")}>
              Pay Now
            </button>
            <div className="new-lj-modal-divider">or use coupon</div>
            <button className="new-lj-modal-btn secondary" onClick={openCouponModal}>
              Enter Coupon Code
            </button>
          </div>
        </div>
      )}

      {/* Coupon code submission modal */}
      {isCouponModalOpen && (
        <div className="new-lj-modal-overlay">
          <div className="new-lj-modal">
            <button onClick={closeModal} className="new-lj-modal-close">×</button>
            <h3>Unlock with Coupon</h3>
            <p>Please enter your university details and validation coupon code.</p>

            <div className="new-lj-coupon-form">
              <div className="new-lj-form-group">
                <label>Universities:</label>
                <select
                  className="new-lj-select"
                  value={selectedUniversity}
                  onChange={(e) => setSelectedUniversity(e.target.value)}
                >
                  <option value="">Select University</option>
                  {Array.isArray(Universitydata) && Universitydata.map((item, index) => (
                    <option key={index} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="new-lj-form-group">
                <label>Level:</label>
                <select
                  className="new-lj-select"
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                >
                  <option value="">Select Level</option>
                  {Array.isArray(Leveldata) && Leveldata.map((item, index) => (
                    <option key={index} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="new-lj-form-group">
                <label>Coupon Code:</label>
                <input
                  type="text"
                  className="new-lj-input"
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                />
              </div>

              <button className="new-lj-modal-btn primary" onClick={verifyCoupon} style={{ marginTop: "10px" }}>
                Submit Coupon
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NewLearningJourney;
